/**
 * Image Upload Component
 */

import { IBuilderComponent } from '../../base'
import { formatFromVariableText } from '../../format-utils'
import type { VariableTextValue } from '../../types'
import { deepMerge, parseToVariableText } from '../../parser-utils'
import imageUploadTemplate from './image-upload.template.json'

export interface IImageUploadComponentSettings {
  // Data settings
  'image-upload-data-value': string // Column ID
  'image-upload-data-label': VariableTextValue

  // Behavior settings
  'image-upload-allowed-file-types': string[]
  'image-upload-behavior-max-items': number
  'image-upload-behavior-max-size': {
    unit: string
    value: number
  }
  'image-upload-behavior-compression-max-size': number[]
  'image-upload-behavior-show-help-text': boolean
  'image-upload-behavior-required'?: boolean
}

export interface IImageUploadComponent extends IBuilderComponent {
  componentId: 'image-upload'
  groupId: 'form'
  settings: IImageUploadComponentSettings
}

/**
 * Image Upload AI Input
 */
export interface AiImageUploadInput {
  columnId: string // Required: maps to table column
  label?: string[] // Format: ["text:Upload Images"] or ["text:Add ", "cr:imageType"]
  maxItems?: number // Maximum number of images
  maxSize?: number // Maximum size in MB
  allowedTypes?: string[] // Allowed file types
  compressionMaxSize?: number // Compression max size in KB
  required?: boolean // Is this field required?
}

const IMAGE_UPLOAD_DEFAULT_TEMPLATE = imageUploadTemplate as unknown as IImageUploadComponentSettings

export class ImageUploadComp {
  getTemplate(): IImageUploadComponentSettings {
    return IMAGE_UPLOAD_DEFAULT_TEMPLATE
  }

  /**
   * format image upload component settings to AI image upload input
   */
  format(settings: IImageUploadComponentSettings): AiImageUploadInput {
    const result: AiImageUploadInput = {
      columnId: settings['image-upload-data-value'],
    }

    // Label
    const label = formatFromVariableText(settings['image-upload-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Max Items
    if (settings['image-upload-behavior-max-items'] !== undefined) {
      result.maxItems = settings['image-upload-behavior-max-items']
    }

    // Max Size (convert to MB)
    if (settings['image-upload-behavior-max-size']) {
      const maxSize = settings['image-upload-behavior-max-size']
      let sizeInMB = maxSize.value
      if (maxSize.unit === 'gb') {
        sizeInMB = maxSize.value * 1024
      } else if (maxSize.unit === 'kb') {
        sizeInMB = maxSize.value / 1024
      }
      result.maxSize = sizeInMB
    }

    // Allowed Types
    if (settings['image-upload-allowed-file-types'] && settings['image-upload-allowed-file-types'].length > 0) {
      result.allowedTypes = settings['image-upload-allowed-file-types']
    }

    // Compression Max Size (convert from array to number)
    if (settings['image-upload-behavior-compression-max-size'] && settings['image-upload-behavior-compression-max-size'].length > 0) {
      result.compressionMaxSize = settings['image-upload-behavior-compression-max-size'][0]
    }

    // Required
    if (settings['image-upload-behavior-required'] !== undefined) {
      result.required = settings['image-upload-behavior-required']
    }

    return result
  }

  /**
   * parse AI image upload input to image upload component settings
   */
  parse(input: AiImageUploadInput): IImageUploadComponentSettings {
    const result = deepMerge(IMAGE_UPLOAD_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['image-upload-data-value'] = input.columnId
    }

    // Data - Label
    if (input.label) {
      result['image-upload-data-label'] = parseToVariableText(input.label)
    }

    // Behavior - Max Items
    if (input.maxItems !== undefined) {
      result['image-upload-behavior-max-items'] = input.maxItems
    }

    // Behavior - Max Size
    if (input.maxSize !== undefined) {
      result['image-upload-behavior-max-size'] = {
        unit: 'mb',
        value: input.maxSize,
      }
    }

    // Behavior - Allowed File Types
    if (input.allowedTypes) {
      result['image-upload-allowed-file-types'] = input.allowedTypes
    }

    // Behavior - Compression Max Size
    if (input.compressionMaxSize !== undefined) {
      result['image-upload-behavior-compression-max-size'] = [input.compressionMaxSize]
    }

    return result
  }
}

